import { useEffect, useState, useRef } from "react";
import "./App.css";

import { RouterProvider } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";

import { AxiosInterceptor } from "./hook/request";
import { authenticateApp } from "./hook/auth-context";
import { useAppState } from "./hook/app-state";
import { initRouting } from "./routes/AppRoutes";
import InitialLoader from "./components/loaders/IntialLoader";
import ConfirmationScreen from "./components/loaders/ConfirmationScreen";
import authService from "./services/authService";
import strapiUserDataManager from "./apis/deletionHelper";
import { healthScoreGroups, prodClient, scoreImageMap } from "./utils/constants";

function App() {
  const { clinicUserId, initializingApp } = authenticateApp();
  const {
    theme,
    currentClinic: currentUser,
    setCurrentClinic,
    showConfirmationScreen,
    appStartloading,
    setAppStartloading,
  } = useAppState();

  const [router, setRouter] = useState<any>(null);
  const [isRouterInitialized, setIsRouterInitialized] = useState(false);

  // Use refs to track initialization state
  const hasInitializedRouter = useRef(false);
  const initializationInProgress = useRef(false);

  // Initialize router only once when the app starts
  useEffect(() => {
    const initializeRouter = async () => {
      // Prevent multiple simultaneous initializations
      if (initializationInProgress.current || hasInitializedRouter.current) {
        return;
      }

      initializationInProgress.current = true;
      setAppStartloading(true);

      try {
        // Set clinic - this should only happen once
        const clinic = prodClient;
        setCurrentClinic(clinic);

        // Initialize router
        const initializedRouter = initRouting(clinic);

        // // TODO: these are the delete apis. Please uncomment with test userIds
        // const result = await strapiUserDataManager.deleteAllUserData(
        //   // "8600097088", // vishal
        //   // "8583825651", // arpita
        //   // "7488308475", // aakash

        //   // "8981620902", // arpita
        //   // "8789982675", //sourabh
        //   // "9810004783" //sir
        //   // "9928739427",
        //   // "9595922002",
        //   "9999911111",
        //   // "9928739427",
        //   prodClient,
        //   [
        //     "Medical History 1",
        //     "Medical History 2",
        //     "Prakriti",
        //     ...healthScoreGroups[0].map((e) => e.name),
        //   ]
        //   // ["Prakriti"]
        // );
        // // // // console.log("Deletion completed:", result);
        // // await strapiUserDataManager.deleteAllScoreQuestionResponsesForAllScores(
        // //   "8789982675"
        // // );
        // healthScoreGroups[0].push({
        //   name: "Prakriti",
        //   logo: scoreImageMap("Physical Activity Score"),
        //   hasFilled: false,
        //   hasPartiallyFilled: false,
        // });
        // await strapiUserDataManager.fetchAllScoreLabels("9999911111", 
        //   healthScoreGroups[0]
        // );
        // await strapiUserDataManager.deleteAllUserData("7488308475");
        // Simulate the delay you had (you can remove this if not needed)
        await new Promise((resolve) => setTimeout(resolve, 500));

        setRouter(initializedRouter);
        setIsRouterInitialized(true);
        hasInitializedRouter.current = true;
      } catch (error) {
        console.error("Failed to initialize router:", error);
        // Reset flags on error so we can try again
        hasInitializedRouter.current = false;
      } finally {
        initializationInProgress.current = false;
        setAppStartloading(false);
      }
    };

    // Only initialize after auth initialization is complete
    if (!initializingApp) {
      initializeRouter();
    }
  }, [initializingApp, setCurrentClinic, setAppStartloading]);

  // Show loading screen
  if (initializingApp || appStartloading || !isRouterInitialized || !router) {
    return <InitialLoader />;
  }

  // Show confirmation screen
  if (showConfirmationScreen) {
    return <ConfirmationScreen />;
  }

  return (
    <ThemeProvider theme={theme}>
      <AxiosInterceptor>
        <RouterProvider router={router} />
      </AxiosInterceptor>
    </ThemeProvider>
  );
}

export default App;
